import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core";
import { I18n } from "@aws-amplify/core";
import { map, get, uniqBy, forEach, sum, filter, flatten, size } from "lodash";
import clsx from "clsx";
import { useReactToPrint } from "react-to-print";
import Box from "@material-ui/core/Box";

import { fetchPost } from "../../../api/fetch";
import { setBreadcrumb } from "../../../actions/common";
import OnsiteCountsForm from "./OnsiteCountsForm";
import ProfilesBox from "./ProfilesBox";
import OnsiteCountsDetails from "./OnsiteCountsDetails";
import PrintIcon from "../../Common/PrintIcon";
import { formatDate } from "../../../utils/date";

const styles = {
  container: {
    margin: "24px",
    minHeight: "unset",
    height: "calc(100vh - 196px)",
    overflow: "auto",
    padding: "24px",
  },
  content: {
    marginTop: "24px",
    maxWidth: "815px",
  },
  gridLine1: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(373px, 1fr))",
    gridAutoRows: "234px",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
  gridLine2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(638px, 1fr))",
    gridAutoRows: "460px",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
  gridLine3: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridAutoRows: "max-content",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
};

const OnsiteCounts = (props) => {
  const { classes, dispatch, language } = props;
  const [data, setData] = React.useState();
  const [filters, setFilters] = React.useState();
  const [expandedSites, setExpandedSites] = React.useState([]);
  const printRef = React.useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  React.useEffect(() => {
    dispatch(
      setBreadcrumb([
        {
          name: "dashboards",
          display: false,
        },
        {
          name: I18n.get("sidebar_onsiteCounts"),
        },
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async (filters) => {
    setFilters(filters);
    try {
      setData();

      let body = {
        filters: {
          active: true,
          company_ids: [get(filters, ["company"])],
          site_ids: get(filters, ["sites"]),
          profile_type_ids: get(filters, ["profileTypes"]),
        },
      };
      setFilters(body.filters);
      let res = await fetchPost("onsite/totals", body);

      // Create tree hierarchy with the result
      let data = {
        sites: uniqBy(
          map(get(res, ["counts"]), (zone) => ({
            company_id: get(zone, ["company_id"]),
            company_name: get(zone, ["company_name"]),
            site_id: get(zone, ["site_id"]),
            site_name: get(zone, ["site_name"]),
            zones: [],
          })),
          "site_id"
        ),
      };

      forEach(get(res, ["counts"]), (zone) => {
        forEach(get(res, ["profile_types"]), (profile_type) => {
          if (
            size(
              filter(
                get(zone, ["profile_types"]),
                (t) => t.profile_type_id === profile_type.id
              )
            ) === 0
          ) {
            zone.profile_types.push({
              profile_type_id: profile_type.id,
              profile_count: null,
            });
          }
        });
      });

      forEach(get(data, ["sites"]), (site) => {
        // Add zones
        site.zones = filter(
          get(res, ["counts"]),
          (zone) => zone.site_id === site.site_id
        );

        // Create total counts for each site
        site.profile_types = [];
        forEach(get(res, ["profile_types"]), (profile_type) => {
          site.profile_types.push({
            profile_type_id: profile_type.id,
            profile_count: sum(
              map(
                filter(
                  flatten(map(site.zones, (z) => z.profile_types)),
                  (t) => t.profile_type_id === profile_type.id
                ),
                (c) => c.profile_count
              )
            ),
          });
        });
      });

      // Create global total counts
      data.profile_types = [];
      forEach(get(res, ["profile_types"]), (profile_type) => {
        data.profile_types.push({
          id: profile_type.id,
          profile_type_name: get(profile_type, [`name_${language}`]),
          profile_count: sum(
            map(
              filter(
                flatten(map(data.sites, (s) => s.profile_types)),
                (t) => t.profile_type_id === profile_type.id
              ),
              (c) => c.profile_count
            )
          ),
        });
      });
      setData(data);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Box className={`mainContent ${classes.container}`}>
      <OnsiteCountsForm search={search} language={language} />
      <Box className={clsx(classes.content, "print-margin")} ref={printRef}>
        {data && (
          <Box style={{ textAlign: "right" }}>
            <PrintIcon handlePrint={handlePrint} />
          </Box>
        )}
        <Box className="only-print">{formatDate(Date.now())}</Box>
        <ProfilesBox
          title={I18n.get("dashboard_totalOnsiteCounts")}
          data={data}
          filters={filters}
        />
        <OnsiteCountsDetails
          data={data}
          refresh={() =>
            search({
              company: get(filters, ["company_ids", 0]),
              sites: get(filters, ["site_ids"]),
              profileTypes: get(filters, ["profile_type_ids"]),
            })
          }
          expandedSites={expandedSites}
          setExpandedSites={setExpandedSites}
        />
        <Box className="only-print">{window.location.href}</Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(OnsiteCounts));
